/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/ScopeAgGridTabStrip.tsx
 * FILE_ID: QB-AGGRID-TABSTRIP-001
 * PURPOSE: SAM-style horizontal tab strip across the top of the
 *          ScopeAgGridEditor. Each section is a tab. Click switches
 *          the active section. Trailing "+ Add" button creates a new
 *          section and immediately switches to it.
 * WHY: SAM's quote workbook has a LEGACY IMPORT / + Add tab strip
 *      that lets one quote contain N independently-editable sheets.
 *      Quotech's existing model (a Quote with a flat list of
 *      QuoteSections) already supports this — we just had no UI to
 *      navigate it as tabs.
 * FLOW:
 *   ┌────────────────────┐    ┌────────────────────┐    ┌────────────────────┐
 *   │ sections[]         │ →  │ tab buttons + Add  │ →  │ onActivate(id)     │
 *   │ activeSectionId    │    │ button             │    │ onAdd()            │
 *   └────────────────────┘    └────────────────────┘    └────────────────────┘
 * RELATED:
 *   - QB-AGGRID-EDITOR-001 (ScopeAgGridEditor) — consumer
 */

import { Plus, Layers } from 'lucide-react'
import type { QuoteSection } from '@/lib/quoteApi'
import { getSectionColor } from './section-color-palette'

export interface ScopeAgGridTabStripProps {
  sections: QuoteSection[]
  activeSectionId: string | null
  isEditable: boolean
  onActivate: (sectionId: string) => void
  onAdd: () => void
}

export default function ScopeAgGridTabStrip({
  sections,
  activeSectionId,
  isEditable,
  onActivate,
  onAdd,
}: ScopeAgGridTabStripProps) {
  return (
    <div
      role="tablist"
      aria-label="Quote sheets"
      className="flex items-center gap-0.5 border-b border-slate-200 dark:border-slate-700 overflow-x-auto bg-slate-50/40 dark:bg-slate-900/40 px-1"
    >
      {sections.map((section) => {
        const isActive = section.id === activeSectionId
        const itemCount = section.items?.length ?? 0
        // Stable per-section colour band — same id always maps to the
        // same Tailwind classes, so the user can scan a multi-sheet
        // quote and tell sections apart at a glance (SAM parity).
        const band = getSectionColor(section.id)
        return (
          <button
            key={section.id}
            role="tab"
            type="button"
            aria-selected={isActive}
            aria-controls={`scope-grid-${section.id}`}
            onClick={() => onActivate(section.id)}
            className={
              isActive
                ? `flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold border-b-2 ${band.tabUnderline} text-slate-900 dark:text-white bg-white dark:bg-slate-900 rounded-t-md whitespace-nowrap`
                : 'flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium border-b-2 border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/40 dark:hover:bg-slate-800/40 rounded-t-md whitespace-nowrap'
            }
          >
            <span
              aria-hidden
              className={`inline-block w-2 h-2 rounded-full shrink-0 ${band.dot}`}
            />
            <Layers className="w-3 h-3 shrink-0 text-slate-400 dark:text-slate-500" />
            <span className="max-w-[180px] truncate">{section.title}</span>
            <span className="text-[10px] text-slate-400 dark:text-slate-500">
              · {itemCount}
            </span>
          </button>
        )
      })}
      {isEditable && (
        <button
          type="button"
          title="Add a new sheet to this quote"
          onClick={onAdd}
          className="flex items-center gap-1 px-2.5 py-1.5 text-[12px] font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30 rounded-t-md border-b-2 border-transparent whitespace-nowrap"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Add sheet</span>
        </button>
      )}
    </div>
  )
}
